import { action, observable } from "mobx";
import { RegistrationFormData } from "@data-types/membership/registration";
import { LugasErrorCodes } from "@data-types/user/LugasErrorCodes";
import { registrationValidationSingle } from "@utils/membership/registrationValidation";
import { MainRegistrationStore } from "../MainRegistrationStore";
import { PlatformRegistrationApiService } from "@api-services/platform";
import { analyticsTracker } from "@utils/analytics";
import { RegistrationTypeEnum } from "@data-types/analytics-tracker/RegistrationTypeEnum";
import { RegistrationInteractionTypeEnum } from "@data-types/analytics-tracker/RegistrationInteractionTypeEnum";
import {
	captureRegistrationCampaign,
	startRegistrationSession,
} from "@utils/analytics/registrationCampaignStorage";
import { registrationInteractionTracker } from "@utils/analytics/registrationInteractionTracker";
import { getCurrentCulture } from "@utils";
import { redirect } from "@state/utils";
import { BankIdStorageKey } from "@utils/specific/bankid/bankIdConstants";

export default class RegistrationStore extends MainRegistrationStore {
	@observable takeoverTempInfoId: string | null = null;

	constructor() {
		super();

		this.isRegistrationInteractionTrackingEnabled = true;
	}

	@action.bound
	onInitialize() {
		captureRegistrationCampaign();

		if (App.state.rootStore.isIpRestricted) {
			return;
		}

		startRegistrationSession();
		registrationInteractionTracker.attach();

		analyticsTracker.trackRegistrationInteraction(
			RegistrationInteractionTypeEnum.PageLanded
		);

		analyticsTracker.trackRegistrationStarted(
			RegistrationTypeEnum.NormalRegistration
		);
		this.fetchLookups();
	}

	@action.bound
	async register(
		formData: RegistrationFormData,
		affiliateCode: string | null
	) {
		analyticsTracker.trackRegistrationInteraction(
			RegistrationInteractionTypeEnum.SubmitClicked
		);

		analyticsTracker.trackRegistrationCampaignSubmit(
			RegistrationTypeEnum.NormalRegistration
		);

		try {
			this.loader.suspend();

			const body = this.prepareRegistrationFormData(
				formData,
				affiliateCode
			);

			this.addCampaignAttribution(body);

			const additionalBankIdUserInfo = sessionStorage.getItem(
				BankIdStorageKey.AdditionalUserInfo
			);

			if (additionalBankIdUserInfo) {
				body.isExternalProviderRegistration = true;
			}

			const response =
				await PlatformRegistrationApiService.registerUser(body);

			if (
				response?.errorCode ===
				LugasErrorCodes.LugasPossibleAccountMatch
			) {
				this.handleLugasMatching(response);
				return;
			}

			if (
				response?.errorCode ===
				LugasErrorCodes.LugasLimitTakeoverNoExistingLimit
			) {
				this.takeoverTempInfoId = response.tempInfoId;
				return;
			}

			this.isRegistrationSuccessful = true;
			analyticsTracker.trackRegistrationFormCompleted();

			analyticsTracker.trackRegistrationInteraction(
				RegistrationInteractionTypeEnum.SubmitSuccess
			);
		} catch (error) {
			console.error(error);

			analyticsTracker.trackRegistrationInteraction(
				RegistrationInteractionTypeEnum.SubmitFailed
			);

			registrationValidationSingle(error?.data);
		} finally {
			this.loader.resume();
			document.querySelector("main")?.scrollTo(0, 0);
		}
	}

	@action.bound
	handleLugasMatching(userMatchingData: {
		possibleMatches: Array<{
			offlineAgencyUser: {
				userName: string;
			};
		}>;
		tempInfoId: string;
	}) {
		const possibleMatchesNames = userMatchingData.possibleMatches
			.map((match) => match.offlineAgencyUser?.userName)
			.filter(Boolean);

		const filteredMatchingData = {
			possibleMatches: possibleMatchesNames,
			tempInfoId: userMatchingData.tempInfoId,
		};

		App.state.rootStore.userAuthStore.userMatchingStore.setUserRegistrationMatchingData(
			filteredMatchingData
		);

		redirect(`/${getCurrentCulture()}/auth/user-matching`);
	}

	@action.bound
	async completeTakeoverWithLimit(limitAmount?: string | null) {
		if (!this.takeoverTempInfoId || !limitAmount) {
			return;
		}

		try {
			this.loader.suspend();

			await PlatformRegistrationApiService.completeTakeoverWithLimit({
				tempInfoId: this.takeoverTempInfoId,
				limitAmount: limitAmount,
			});

			this.takeoverTempInfoId = null;
			this.isRegistrationSuccessful = true;
			analyticsTracker.trackRegistrationFormCompleted();

			analyticsTracker.trackRegistrationInteraction(
				RegistrationInteractionTypeEnum.SubmitSuccess
			);
		} catch (error) {
			console.error(error);

			analyticsTracker.trackRegistrationInteraction(
				RegistrationInteractionTypeEnum.SubmitFailed
			);

			registrationValidationSingle(error?.data);
		} finally {
			this.loader.resume();
			document.querySelector("main")?.scrollTo(0, 0);
		}
	}

	@action.bound
	setIsRegistrationSuccessful(isSuccessful: boolean) {
		this.isRegistrationSuccessful = isSuccessful;
	}

	@action.bound
	onDispose() {
		registrationInteractionTracker.detach();
		this.currentStepReactionDisposer?.();

		this.currentStep = 1;
		this.isRegistrationSuccessful = null;
		this.takeoverTempInfoId = null;

		//lookups
		this.titleData = [];
		this.countryData = [];
		this.languageData = [];
		this.countryRegionsData = [];
		this.gamingLimitsData = null;
	}
}
